import { List } from 'immutable';
import { IDispatchId, IRequestError } from '../../utils/helpers/redux.helper';
import { Document } from '../document-data/document.model';
import { IRendererOptions } from '../renderer/renderer.interface';
import {
  IAddPaymentPayload,
  PayPalTransactionItem,
} from './payments-data.data';

// action enums
export enum ActionsEnum {
  SEND_REMINDERS_PREVIEW = 'SEND_REMINDERS_PREVIEW',
  SEND_INVOICE_UNOPENED_PREVIEW = 'SEND_INVOICE_UNOPENED_PREVIEW',
  SEND_RECEIPT = 'SEND_RECEIPT',
  SEND_RECEIPT_SUCCESS = 'SEND_RECEIPT_SUCCESS',
  SEND_RECEIPT_ERROR = 'SEND_RECEIPT_ERROR',
  SEND_RECEIPT_PREVIEW = 'SEND_RECEIPT_PREVIEW',
  SEND_RECEIPT_PREVIEW_SUCCESS = 'SEND_RECEIPT_PREVIEW_SUCCESS',
  SEND_RECEIPT_PREVIEW_ERROR = 'SEND_RECEIPT_PREVIEW_ERROR',
  GET_TRANSACTION_HISTORY_URL = 'GET_TRANSACTION_HISTORY_URL',
  GET_TRANSACTION_HISTORY_URL_SUCCESS = 'GET_TRANSACTION_HISTORY_URL_SUCCESS',
  GET_TRANSACTION_HISTORY_URL_ERROR = 'GET_TRANSACTION_HISTORY_URL_ERROR',
  TRIGGER_STRIPE_ONBOARDING_CANVAS = 'TRIGGER_STRIPE_ONBOARDING_CANVAS',
  // PayPal
  TRIGGER_PAYPAL_ONBOARDING_CANVAS = 'TRIGGER_PAYPAL_ONBOARDING_CANVAS',
  FETCH_PAYPAL_TRANSACTION_HISTORY = 'FETCH_PAYPAL_TRANSACTION_HISTORY',
  FETCH_PAYPAL_TRANSACTION_HISTORY_SUCCESS = 'FETCH_PAYPAL_TRANSACTION_HISTORY_SUCCESS',
  FETCH_PAYPAL_TRANSACTION_HISTORY_ERROR = 'FETCH_PAYPAL_TRANSACTION_HISTORY_ERROR',
}

// action payload definitions
export interface ISendReceipt extends IDispatchId {
  documentId: string;
  recipient: string;
  transactionId: string;
  rendererOptions: IRendererOptions;
  updatePaymentReceiptsSettings: boolean;
}

export interface ISendReceiptSuccess extends IDispatchId {
  recipient: string;
}

export interface ISendReceiptPreview extends IDispatchId {
  document: Document;
  transaction: IAddPaymentPayload;
  rendererOptions: IRendererOptions;
}

export interface ISendRemindersPreview {
  reminderDays: List<number>;
}

export interface IGetTransactionHistoryUrl extends IDispatchId {
  documentId: string;
  transactionId: string;
}

export interface IGetTransactionHistoryUrlSuccess extends IDispatchId {}

export interface IGetTransactionHistoryUrlError
  extends IDispatchId,
    IRequestError {}

export interface ITriggerStripeOnboardingCanvas {
  enableCardPayments: boolean;
  href?: string;
}

export interface ITriggerPayPalOnboardingCanvasPayload {
  returnUrl?: string;
}

export interface IFetchPayPalTransactionHistoryPayload {
  timestamp?: string;
}

export interface IFetchPayPalTransactionHistorySuccessPayload {
  transactions: List<PayPalTransactionItem>;
  nextPageTimestamp?: string;
}

// action definitions
export interface SendRemindersPreview {
  type: ActionsEnum.SEND_REMINDERS_PREVIEW;
  payload: ISendRemindersPreview;
}

export interface SendInvoiceUnopenedPreview {
  type: ActionsEnum.SEND_INVOICE_UNOPENED_PREVIEW;
}

export interface SendReceipt {
  type: ActionsEnum.SEND_RECEIPT;
  payload: ISendReceipt;
}

export interface SendReceiptSuccess {
  type: ActionsEnum.SEND_RECEIPT_SUCCESS;
  payload: ISendReceiptSuccess;
}

export interface SendReceiptError {
  type: ActionsEnum.SEND_RECEIPT_ERROR;
  payload: IRequestError;
}

export interface SendReceiptPreview {
  type: ActionsEnum.SEND_RECEIPT_PREVIEW;
  payload: ISendReceiptPreview;
}

export interface SendReceiptPreviewSuccess {
  type: ActionsEnum.SEND_RECEIPT_PREVIEW_SUCCESS;
  payload: IDispatchId;
}

export interface SendReceiptPreviewError {
  type: ActionsEnum.SEND_RECEIPT_PREVIEW_ERROR;
  payload: IRequestError;
}

export interface GetTransactionHistoryUrl {
  type: ActionsEnum.GET_TRANSACTION_HISTORY_URL;
  payload: IGetTransactionHistoryUrl;
}

export interface GetTransactionHistoryUrlSuccess {
  type: ActionsEnum.GET_TRANSACTION_HISTORY_URL_SUCCESS;
  payload: IGetTransactionHistoryUrlSuccess;
}

export interface GetTransactionHistoryUrlError {
  type: ActionsEnum.GET_TRANSACTION_HISTORY_URL_ERROR;
  payload: IGetTransactionHistoryUrlError;
}

export interface TriggerStripeOnboardingCanvas {
  type: ActionsEnum.TRIGGER_STRIPE_ONBOARDING_CANVAS;
  payload: ITriggerStripeOnboardingCanvas;
}

export interface ITriggerPayPalOnboardingCanvas {
  type: ActionsEnum.TRIGGER_PAYPAL_ONBOARDING_CANVAS;
  payload: ITriggerPayPalOnboardingCanvasPayload;
}

export interface IFetchPayPalTransactionHistory {
  type: ActionsEnum.FETCH_PAYPAL_TRANSACTION_HISTORY;
  payload: IFetchPayPalTransactionHistoryPayload;
}

export interface IFetchPayPalTransactionHistorySuccess {
  type: ActionsEnum.FETCH_PAYPAL_TRANSACTION_HISTORY_SUCCESS;
  payload: IFetchPayPalTransactionHistorySuccessPayload;
}

export interface IFetchPayPalTransactionHistoryError {
  type: ActionsEnum.FETCH_PAYPAL_TRANSACTION_HISTORY_ERROR;
  payload: {
    error: Error;
  };
}

// action creators
export const sendRemindersPreview = (
  reminderDays: List<number>
): SendRemindersPreview => ({
  type: ActionsEnum.SEND_REMINDERS_PREVIEW,
  payload: {
    reminderDays,
  },
});

export const sendInvoiceUnopenedPreview = (): SendInvoiceUnopenedPreview => ({
  type: ActionsEnum.SEND_INVOICE_UNOPENED_PREVIEW,
});

export const sendReceipt = (
  dispatchId: string,
  documentId: string,
  recipient: string,
  transactionId: string,
  rendererOptions: IRendererOptions,
  updatePaymentReceiptsSettings: boolean
): SendReceipt => ({
  type: ActionsEnum.SEND_RECEIPT,
  payload: {
    dispatchId,
    documentId,
    recipient,
    transactionId,
    rendererOptions,
    updatePaymentReceiptsSettings,
  },
});

export const sendReceiptSuccess = (
  dispatchId: string,
  recipient: string
): SendReceiptSuccess => ({
  type: ActionsEnum.SEND_RECEIPT_SUCCESS,
  payload: {
    dispatchId,
    recipient,
  },
});

export const sendReceiptError = (
  dispatchId: string,
  error: Error
): SendReceiptError => ({
  type: ActionsEnum.SEND_RECEIPT_ERROR,
  payload: {
    dispatchId,
    error,
  },
});

export const sendReceiptPreview = (
  dispatchId: string,
  document: Document,
  transaction: IAddPaymentPayload,
  rendererOptions: IRendererOptions
): SendReceiptPreview => ({
  type: ActionsEnum.SEND_RECEIPT_PREVIEW,
  payload: {
    dispatchId,
    document,
    transaction,
    rendererOptions,
  },
});

export const sendReceiptPreviewSuccess = (
  dispatchId: string
): SendReceiptPreviewSuccess => ({
  type: ActionsEnum.SEND_RECEIPT_PREVIEW_SUCCESS,
  payload: {
    dispatchId,
  },
});

export const sendReceiptPreviewError = (
  dispatchId: string,
  error: Error
): SendReceiptPreviewError => ({
  type: ActionsEnum.SEND_RECEIPT_PREVIEW_ERROR,
  payload: {
    dispatchId,
    error,
  },
});

export const getTransactionHistoryUrl = (
  dispatchId: string,
  documentId: string,
  transactionId: string
): GetTransactionHistoryUrl => ({
  type: ActionsEnum.GET_TRANSACTION_HISTORY_URL,
  payload: {
    dispatchId,
    documentId,
    transactionId,
  },
});

export const getTransactionHistoryUrlSuccess = (
  dispatchId: string
): GetTransactionHistoryUrlSuccess => ({
  type: ActionsEnum.GET_TRANSACTION_HISTORY_URL_SUCCESS,
  payload: {
    dispatchId,
  },
});

export const getTransactionHistoryUrlError = (
  dispatchId: string,
  error: Error
): GetTransactionHistoryUrlError => ({
  type: ActionsEnum.GET_TRANSACTION_HISTORY_URL_ERROR,
  payload: {
    dispatchId,
    error,
  },
});

export const triggerStripeOnboardingCanvas = (
  enableCardPayments: boolean,
  href?: string
): TriggerStripeOnboardingCanvas => ({
  type: ActionsEnum.TRIGGER_STRIPE_ONBOARDING_CANVAS,
  payload: {
    enableCardPayments,
    href,
  },
});

export const triggerPayPalOnboardingCanvas = (
  returnUrl?: string
): ITriggerPayPalOnboardingCanvas => ({
  type: ActionsEnum.TRIGGER_PAYPAL_ONBOARDING_CANVAS,
  payload: {
    returnUrl,
  },
});

export const fetchPayPalTransactionHistory = (
  timestamp?: string
): IFetchPayPalTransactionHistory => ({
  type: ActionsEnum.FETCH_PAYPAL_TRANSACTION_HISTORY,
  payload: {
    timestamp,
  },
});

export const fetchPayPalTransactionHistorySuccess = (
  transactions: List<PayPalTransactionItem>,
  nextPageTimestamp?: string
): IFetchPayPalTransactionHistorySuccess => ({
  type: ActionsEnum.FETCH_PAYPAL_TRANSACTION_HISTORY_SUCCESS,
  payload: {
    transactions,
    nextPageTimestamp,
  },
});

export const fetchPayPalTransactionHistoryError = (
  error: Error
): IFetchPayPalTransactionHistoryError => ({
  type: ActionsEnum.FETCH_PAYPAL_TRANSACTION_HISTORY_ERROR,
  payload: {
    error,
  },
});
